import type { ConsoleBrandDetail } from '@rsecure/brand-contracts/console';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Badge, ErrorState, Loading, Ltr, Notice } from '../../components/ui';
import { api } from '../../lib/api';

export function PreviewTab({ d }: { d: ConsoleBrandDetail }) {
  const [kind, setKind] = useState<'email' | 'landing'>('email');
  const q = useQuery({
    queryKey: ['preview', d.entity.slug],
    queryFn: () => api.preview(d.entity.slug),
    enabled: !!d.published,
  });
  if (!d.published)
    return (
      <Notice tone="warn">No published revision — there are no published assets to preview.</Notice>
    );
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading />;
  const i = q.data.info;
  const src = `/bff/preview/${encodeURIComponent(d.entity.slug)}/${kind}`;
  return (
    <div className="grid" style={{ gap: 14 }}>
      <Notice>
        <strong>Illustrative appearance preview.</strong> Built with the actual Brand Engine — the
        same profile derivation (<span className="mono">toBrandProfile</span>), blueprint
        composition and renderers that produce real campaign content — using this brand’s published
        assets and fixed placeholder copy. It is <strong>not</strong> a generated campaign: no
        scenario intelligence, no AI body, no personalization, no tracking.
      </Notice>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <fieldset className="seg" aria-label="Surface">
          {(['email', 'landing'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={kind === k} onClick={() => setKind(k)}>
              {k === 'email' ? 'Email' : 'Landing page'}
            </button>
          ))}
        </fieldset>
        <Badge tone={i.fidelity === 'registered' ? 'ok' : 'warn'}>{i.fidelity} fidelity</Badge>
      </div>
      <div className="split">
        <iframe
          key={src}
          title={`${kind} appearance preview`}
          className="preview-frame"
          src={src}
          sandbox=""
          referrerPolicy="no-referrer"
          data-testid="preview-frame"
        />
        <section className="card drawer" aria-labelledby="h-inputs">
          <div className="card-h">
            <h2 id="h-inputs">What the Brand Engine selected</h2>
          </div>
          <div className="card-b">
            <dl className="dl" style={{ gridTemplateColumns: '130px minmax(0,1fr)' }}>
              <dt>Profile source</dt>
              <dd>
                <Ltr mono>catalog_compat</Ltr>{' '}
                <span className="muted">(BRAND_CATALOG_SOURCE=v3)</span>
              </dd>
              <dt>Primary color</dt>
              <dd>
                <i className="swatch" style={{ background: i.primary }} />
                <Ltr mono>{i.primary}</Ltr> <span className="muted">on {i.onPrimary}</span>
              </dd>
              <dt>Band logo</dt>
              <dd>
                {i.bandLogoUrl ? (
                  <Ltr mono>logo_url (on-dark mark)</Ltr>
                ) : (
                  <span className="faint">none → wordmark on the band</span>
                )}
              </dd>
              <dt>White header</dt>
              <dd>
                {i.whiteHeaderLogoUrl ? (
                  <Ltr mono>logo_light_url (on-light mark)</Ltr>
                ) : (
                  <span className="faint">none → band or wordmark</span>
                )}
              </dd>
              <dt>Language</dt>
              <dd>{i.language === 'he' ? 'Hebrew (region ISRAEL)' : 'English'}</dd>
            </dl>
            <p className="muted" style={{ fontSize: 12.5, margin: '4px 0 0' }}>
              This is exactly the color and logo selection api-server’s{' '}
              <span className="mono">toBrandProfile</span> makes for a real send — Brandfetch accent
              preferred, saturated-dark leads kept, near-black/white leads skipped.
            </p>
            <p className="muted" style={{ fontSize: 12.5, marginBottom: 0 }}>
              Renderer: <Ltr mono>{q.data.renderer}</Ltr>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
